/**
 * ****************************************************************************
 * @description     :   Config route for all app
 * @created at      :   2022/03/24
 * @created by      :   ANS809 – quypn@ans-asia.com
 * @package         :   Frontier Consulting
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

/**
 * Import libraries
 */
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import messages, { MSG_TYPE, MSG_TITLE } from '../utils/messages';

/**
 * Import other part of router
 */
import AuthRoutes from '@/router/auth-router';
import RaRoutes from '@/router/ra-router';
import InquiryRoutes from '@/router/inquiry-router';
import InputRoutes from '@/router/input-router';
import OutputRoutes from '@/router/output-router';
import SystemRouters from '@/router/system-router';
import FaqRoutes from '@/router/faq-router';
/**
 * Import component of view
 */
const PageNotFound = () => import('@/views/common/404');
const ServerError = () => import('@/views/common/500');
const StandardForm = () => import('@/views/common/standardform');

/**
 * Defined some base router
 */
const baseRoutes = [
    {
        path: '/404',
        name: 'PageNotFound',
        component: PageNotFound,
        meta: {
            title: 'エラー 404'
        }
    },
    {
        path: '/500',
        name: 'ServerError',
        component: ServerError,
        meta: {
            title: 'エラー 500'
        }
    },
    {
        path: '/',
        name: 'Dashboard',
        component: StandardForm,
        meta: {
            title: '標準フォーム'
        }
    },
    {
        path: '/standard-form',
        name: 'StandardForm',
        component: StandardForm,
        meta: {
            title: '標準フォーム'
        }
    },
    {
        path: '/:catchAll(.*)', // Unrecognized path automatically matches 404
        redirect: '/404'
    }
];

/**
 * Concat all parts of router
 */
const routes = baseRoutes
    .concat(AuthRoutes)
    .concat(RaRoutes)
    .concat(InquiryRoutes)
    .concat(InputRoutes)
    .concat(OutputRoutes)
    .concat(SystemRouters)
    .concat(FaqRoutes);

/**
 * Defined Vue Router
 */
const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior() {
        return { top: 0 };
    }
});

/**
 * Change title of website on browser when change page
 * -----------------------------------------
 * @author : QuyPN - 2022/03/24 - create
 * @params : to - object - Data of router will redirect to
 * @params : from - object - Data of current router
 * @params : next - function - Continue redirect
 * @access : public
 */
router.beforeEach((to, from, next) => {
    const func_logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('clientcode');
        store.dispatch('app/resetAppState', false);
        store.dispatch('app/setLogout', false);
        store.dispatch('app/setLogin', false);
    };
    const func_beforeEach = (to, from, next) => {
        store.commit('app/hideMenu');
        store.commit('app/hideHeaderError');
        const publicPages = ['/login', '/login-mail', '/mobile/ra300100i'];
        const authRequired = !publicPages.includes(to.path);
        //const isMobile = to.path.startsWith('mobile');
        if (to.meta.isIsMobile) {
            let loggedIn = false;
            const token = localStorage.getItem('tokenMobile');
            const tokenTimeout = localStorage.getItem('tokenTimeoutMobile');
            if (token && tokenTimeout && new Date(tokenTimeout) >= new Date()) {
                loggedIn = true;
            } else {
                localStorage.removeItem('tokenMobile');
                localStorage.removeItem('tokenMobileActive');
                localStorage.removeItem('tokenTimeoutMobile');
                localStorage.removeItem('certificationcode');
            }
            if (authRequired && !loggedIn) {
                localStorage.setItem('beforeUrl', window.location.pathname);
                store.dispatch('app/showModalMessage', {
                    type: MSG_TYPE.ERROR,
                    title: MSG_TITLE.E999,
                    content: messages.E401,
                    callback: () => {
                        return next('/mobile/ra300100i');
                    }
                });
                // return next('/mobile/ra300100i');
            }
            // if (!authRequired && loggedIn) {
            //     //const tokenActive = localStorage.getItem('tokenMobileActive');
            //     const notRedirectMobile = localStorage.getItem('notRedirectMobile');
            //     // if (tokenActive && tokenActive == 1) {
            //     //     return next('/mobile/ra300200i?' + window.location.search);
            //     // }
            //     // if (tokenActive && tokenActive == 2) {
            //     //     return next('/mobile/ra300700i?' + window.location.search);
            //     // }
            //     if (notRedirectMobile == 0) {
            //         return next('/mobile/ra300600v?' + window.location.search);
            //     }
            //     localStorage.setItem('notRedirectMobile', 0);
            // }
        } else {
            let loggedIn = false;
            const token = localStorage.getItem('token');
            const tokenTimeout = localStorage.getItem('tokenTimeout');
            if (token && tokenTimeout && new Date(tokenTimeout) >= new Date()) {
                loggedIn = true;
                store.dispatch('app/setLogin', true);
            } else {
                localStorage.removeItem('token');
                localStorage.removeItem('tokenTimeout');
            }
            // console.log(store._state.data.app.isLogin);
            if (authRequired && !loggedIn) {
                localStorage.setItem('beforeUrl', window.location.pathname);
                if (!store._state.data.app.isLogin) {
                    store.dispatch('app/setLogin', false);
                    return next('/login');
                } else {
                    store.dispatch('app/showModalMessage', {
                        type: MSG_TYPE.ERROR,
                        title: MSG_TITLE.E999,
                        content: messages.E401,
                        callback: () => {
                            store.dispatch('app/setLogin', false);
                            return next('/login');
                        }
                    });
                }
                // return next('/login');
            }
            if (!authRequired && loggedIn) {
                store.dispatch('app/showModalMessage', {
                    type: MSG_TYPE.ERROR,
                    title: MSG_TITLE.E999,
                    content: messages.E401,
                    callback: () => {
                        store.dispatch('app/setLogin', false);
                        return next('/');
                    }
                });
                // return next('/');
            }
        }
        if (to.path == '/') {
            const url = localStorage.getItem('urlAfterLogin');
            if (url) {
                return next(url);
            }
        }
        document.title =
            (to.meta.title || 'FRONTIER CONSULTING RA portal') +
            ' | FRONTIER CONSULTING RA portal';
        next();
    };
    if (store._state.data.app.isDataScreenChange) {
        store.dispatch('app/showModalMessage', {
            type: MSG_TYPE.CONFIRM,
            content: messages.C044,
            okText: 'このページを離れる',
            cancelText: 'キャンセル',
            callback: (ok) => {
                if (ok) {
                    if (store._state.data.app.isLogout) {
                        func_logout();
                    }
                    func_beforeEach(to, from, next);
                    store.dispatch('app/setDataScreenChange', false);
                } else {
                    store.dispatch('app/setLogout', false);
                    next(false);
                }
            }
        });
    } else {
        if (store._state.data.app.isLogout) {
            func_logout();
        }
        func_beforeEach(to, from, next);
    }
});

export default router;
